import type { Evento } from '@/types'

const DEFAULT_EVENT_FIELDS = {
  criador_id: 'mock-user-admin',
  formas_pagamento: ['pix', 'dinheiro', 'cartao'],
  inscricoes_abertas: true,
  limitar_vagas_externas: false,
  ministerio_nome: null,
  numero_parcelas: null,
  permitir_parcelamento: false,
  vagas_externas: null,
  valor_inscricao: null,
  visibilidade: 'interno' as const,
} satisfies Pick<
  Evento,
  | 'criador_id'
  | 'formas_pagamento'
  | 'inscricoes_abertas'
  | 'limitar_vagas_externas'
  | 'ministerio_nome'
  | 'numero_parcelas'
  | 'permitir_parcelamento'
  | 'vagas_externas'
  | 'valor_inscricao'
  | 'visibilidade'
>

type CreateMockEventoInput = Omit<
  Evento,
  | 'criador_id'
  | 'formas_pagamento'
  | 'inscricoes_abertas'
  | 'limitar_vagas_externas'
  | 'ministerio_nome'
  | 'numero_parcelas'
  | 'permitir_parcelamento'
  | 'vagas_externas'
  | 'valor_inscricao'
  | 'visibilidade'
  | 'chave_pix'
  | 'gratuito'
> & {
  slug: string
}

const createMockEvento = (data: CreateMockEventoInput): Evento => {
  const gratuito = data.valor === 0

  return {
    ...DEFAULT_EVENT_FIELDS,
    ...data,
    chave_pix: gratuito ? null : '0000000000',
    gratuito,
    valor_inscricao: gratuito ? null : data.valor,
  }
}

export const mockEventos: Evento[] = [
  createMockEvento({
    id: '1',
    titulo: 'Culto de Celebração',
    descricao: 'Culto especial de celebração com louvor e palavra',
    inicio_em: '2024-11-24T19:00:00Z',
    fim_em: '2024-11-24T21:00:00Z',
    local: 'Templo Principal',
    capacidade: 500,
    ministerio_id: '1',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2024-10-20T10:00:00Z',
    atualizado_em: '2024-10-20T10:00:00Z',
    slug: 'culto-de-celebracao',
    tipo: 'culto',
  }),
  createMockEvento({
    id: '2',
    titulo: 'Célula de Jovens',
    descricao: 'Encontro semanal da grupo de jovens com estudo bíblico',
    inicio_em: '2024-11-22T20:00:00Z',
    fim_em: '2024-11-22T22:00:00Z',
    local: 'Casa do líder',
    capacidade: 30,
    ministerio_id: '2',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2024-10-18T10:00:00Z',
    atualizado_em: '2024-10-18T10:00:00Z',
    slug: 'celula-de-jovens',
    tipo: 'reuniao',
  }),
  createMockEvento({
    id: '3',
    titulo: 'Retiro Espiritual',
    descricao: 'Retiro de fim de semana com ministração e momentos de oração',
    inicio_em: '2024-12-01T08:00:00Z',
    fim_em: '2024-12-03T18:00:00Z',
    local: 'Sítio Recanto da Paz',
    capacidade: 100,
    ministerio_id: '1',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
    status: 'publicado',
    valor: 150,
    criado_em: '2024-10-15T10:00:00Z',
    atualizado_em: '2024-10-15T10:00:00Z',
    slug: 'retiro-espiritual',
    tipo: 'retiro',
  }),
  createMockEvento({
    id: '4',
    titulo: 'Ensaio de Louvor',
    descricao: 'Ensaio do ministério de louvor - aberto para novos integrantes',
    inicio_em: '2024-11-23T14:00:00Z',
    fim_em: '2024-11-23T17:00:00Z',
    local: 'Sala de Ensaio',
    capacidade: 20,
    ministerio_id: '3',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2024-10-19T10:00:00Z',
    atualizado_em: '2024-10-19T10:00:00Z',
    slug: 'ensaio-de-louvor',
    tipo: 'reuniao',
  }),
  createMockEvento({
    id: '5',
    titulo: 'Ação Social - Distribuição de Alimentos',
    descricao: 'Ação de distribuição de cestas básicas para a comunidade',
    inicio_em: '2024-11-25T09:00:00Z',
    fim_em: '2024-11-25T13:00:00Z',
    local: 'Praça Central',
    capacidade: 50,
    ministerio_id: '4',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2024-10-17T10:00:00Z',
    atualizado_em: '2024-10-17T10:00:00Z',
    slug: 'acao-social-distribuicao-de-alimentos',
    tipo: 'evento-especial',
  }),
  createMockEvento({
    id: '6',
    titulo: 'Conferência de Liderança',
    descricao: 'Conferência anual para líderes e aspirantes à liderança.',
    inicio_em: '2024-12-10T09:00:00Z',
    fim_em: '2024-12-12T18:00:00Z',
    local: 'Centro de Convenções da Cidade',
    capacidade: 200,
    ministerio_id: '1',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1522204523234-8729aa6ed03d?w=800',
    status: 'publicado',
    valor: 75,
    criado_em: '2024-11-01T10:00:00Z',
    atualizado_em: '2024-11-01T10:00:00Z',
    slug: 'conferencia-de-lideranca',
    tipo: 'conferencia',
  }),
  createMockEvento({
    id: '7',
    titulo: 'Culto de Ano Novo',
    descricao: 'Celebração especial para a virada do ano com louvor e oração.',
    inicio_em: '2025-01-01T22:00:00Z',
    fim_em: '2025-01-01T23:59:59Z',
    local: 'Templo Principal',
    capacidade: 1000,
    ministerio_id: '1',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1517486804500-be25f1b81471?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2024-11-15T10:00:00Z',
    atualizado_em: '2024-11-15T10:00:00Z',
    slug: 'culto-de-ano-novo',
    tipo: 'culto',
  }),
  createMockEvento({
    id: '8',
    titulo: 'Workshop de Louvor e Adoração',
    descricao: 'Workshop intensivo para aprimorar habilidades musicais e espirituais.',
    inicio_em: '2025-11-13T14:00:00Z',
    fim_em: '2025-11-13T18:00:00Z',
    local: 'Salão de Música',
    capacidade: 40,
    ministerio_id: '3',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1510915362895-4183a3766fac?w=800',
    status: 'publicado',
    valor: 50,
    criado_em: '2025-10-01T10:00:00Z',
    atualizado_em: '2025-10-01T10:00:00Z',
    slug: 'workshop-de-louvor-e-adoracao',
    tipo: 'evento-especial',
  }),
  createMockEvento({
    id: '9',
    titulo: 'Ceia de Natal da Igreja',
    descricao: 'Celebração especial de Natal com a comunidade da igreja.',
    inicio_em: '2025-12-25T19:00:00Z',
    fim_em: '2025-12-25T22:00:00Z',
    local: 'Salão Social',
    capacidade: 300,
    ministerio_id: '1',
    igreja_id: '123e4567-e89b-12d3-a456-426614174002',
    capa_url: 'https://images.unsplash.com/photo-1513885001963-0e04175929e4?w=800',
    status: 'publicado',
    valor: 0,
    criado_em: '2025-11-01T10:00:00Z',
    atualizado_em: '2025-11-01T10:00:00Z',
    slug: 'ceia-de-natal-da-igreja',
    tipo: 'evento-especial',
  }),
]

export const mockEventosPorStatus = {
  publicado: mockEventos.filter((e) => e.status === 'publicado'),
  rascunho: [] as Evento[],
  cancelado: [] as Evento[],
}
